import { useEffect, useRef } from 'react';
import { GameModeCards } from './GameModeCards.jsx';
import { RunPreviewGrid } from './RunPreviewGrid.jsx';

export function RunResetDialog({
  stage,
  preview,
  error,
  acknowledged,
  gameModes,
  selectedGameModeId,
  onGameModeSelect,
  onAcknowledged,
  onCancel,
  onRetry,
  onConfirm,
}) {
  const dialogRef = useRef(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const open = stage !== 'closed';
  const blocking = stage === 'preview-loading' || stage === 'resetting';

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelector('button:not([disabled]), input:not([disabled])');
    focusable?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !blocking) {
        event.preventDefault();
        onCancelRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const items = [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled])')];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
  }, [blocking, open]);

  if (!open) return null;

  return (
    <div
      className="run-reset-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !blocking) onCancel(); }}
    >
      <section ref={dialogRef} className="run-reset-modal glass-panel" role="dialog" aria-modal="true" aria-labelledby="run-reset-title">
        {stage === 'preview-loading' && (
          <div className="run-reset-working" aria-live="polite">
            <div className="dope-spinner" aria-hidden="true"><span /></div>
            <div className="eyebrow">CHECKING THE DAMAGE</div>
            <h2 id="run-reset-title">Reading the fine print…</h2>
            <p>Checking the selected mode, event pack, debt rules, and fresh batch of terrible choices.</p>
          </div>
        )}

        {stage === 'resetting' && (
          <div className="run-reset-working" aria-live="assertive">
            <div className="dope-spinner burn" aria-hidden="true"><span /></div>
            <div className="eyebrow">NO TURNING BACK NOW</div>
            <h2 id="run-reset-title">Burning the evidence…</h2>
            <p>Flushing the stash, forgetting Chaz, dodging the loan shark, and loading your selected run from the server.</p>
          </div>
        )}

        {stage === 'error' && (
          <>
            <div className="eyebrow warning-text">THE RESET HIT A POTHOLE</div>
            <h2 id="run-reset-title">Nothing was erased.</h2>
            <p className="run-reset-error" role="alert">{error || 'The server refused to cooperate.'}</p>
            <div className="run-reset-actions">
              <button className="btn" type="button" onClick={onCancel}>Keep Current Run</button>
              <button className="btn buy" type="button" onClick={onRetry}>Try Again</button>
            </div>
          </>
        )}

        {stage === 'confirm' && preview && (
          <>
            <div className="eyebrow danger-text">DESTRUCTIVE RUN ACTION</div>
            <h2 id="run-reset-title">Reset your current run?</h2>
            <p>
              This abandons the current run and wipes its cash, debt, inventory, stash, bank balance, day, location,
              encounter history, NPC memory, Chaz rides, and run-scoped street log. Your account, login, alias,
              passphrase, interface settings, and all other account data stay intact.
            </p>
            <div className="reset-current-run">
              <span>Current run</span>
              <strong>{preview.current_run?.ruleset || 'unknown'} · day {preview.current_run?.day ?? '?'}</strong>
              <small>{preview.current_run?.game_mode_id || 'classic_30'} · {preview.current_run?.run_id || 'No active run ID'}</small>
            </div>
            <GameModeCards
              modes={gameModes}
              selectedModeId={selectedGameModeId}
              onSelect={onGameModeSelect}
              legend="Choose the replacement run"
            />
            <h3>Your fresh run will receive</h3>
            <RunPreviewGrid preview={preview} />
            <label className="reset-acknowledgement">
              <input type="checkbox" checked={acknowledged} onChange={(event) => onAcknowledged(event.target.checked)} />
              <span><strong>I understand this permanently abandons the current run.</strong><small>Changing modes clears this acknowledgement. The server rechecks the selected rules before replacement.</small></span>
            </label>
            <div className="run-reset-actions">
              <button className="btn" type="button" onClick={onCancel}>Keep My Bad Decisions</button>
              <button className="btn sell reset-confirm-button" type="button" onClick={onConfirm} disabled={!acknowledged}>Yes — Erase This Run</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
